import { createApi } from '@reduxjs/toolkit/query/react';
import { toGridQueryBody } from '@/components/CustomAgGrid/serverSide/gridRequest.ts';
import { appPortalBaseQuery } from '@/features/baseQuery.ts';
import { toScopeBody, withScopeQuery } from '@/features/analytics/analytics-scope.ts';
import type { AnalyticsScope } from '@/features/analytics/analytics-scope.def.ts';
import type {
  BackendFundInvestorsDto,
  BackendFundSummaryDto,
  BackendFundsDashboardRecordsDto,
  BackendFundsDashboardSummaryDto,
  GetFundInvestorsRequest,
  GetFundSummaryRequest,
  GetFundsDashboardRecordsRequest,
} from '@/features/fund-management/fund-management.def.ts';

export const fundManagementApi = createApi({
  reducerPath: 'fundManagementApi',
  baseQuery: appPortalBaseQuery(),
  tagTypes: ['FundsDashboardSummary', 'FundsDashboardRecords', 'FundInvestors'],
  endpoints: (builder) => ({
    getFundsDashboardSummary: builder.query<BackendFundsDashboardSummaryDto, AnalyticsScope | void>(
      {
        query: (scope) => ({
          url: withScopeQuery('/funds/dashboard/summary', scope),
          method: 'GET',
        }),
        providesTags: ['FundsDashboardSummary'],
      },
    ),
    getFundsDashboardRecords: builder.query<
      BackendFundsDashboardRecordsDto,
      GetFundsDashboardRecordsRequest
    >({
      query: ({ page, pageSize, sort, filter, ...scope }) => ({
        url: '/funds/dashboard/records/query',
        method: 'POST',
        body: toGridQueryBody({ page, pageSize, sort, filter }, toScopeBody(scope)),
      }),
      providesTags: ['FundsDashboardRecords'],
    }),
    getFundInvestors: builder.query<BackendFundInvestorsDto, GetFundInvestorsRequest>({
      query: ({ fundId, page, pageSize, sort, filter, ...scope }) => ({
        url: `/funds/${encodeURIComponent(fundId)}/investors/query`,
        method: 'POST',
        body: toGridQueryBody({ page, pageSize, sort, filter }, toScopeBody(scope)),
      }),
      providesTags: (_result, _error, { fundId }) => [{ type: 'FundInvestors', id: fundId }],
    }),
    /** Fund Detail header for the page scope (B-01). */
    getFundSummary: builder.query<BackendFundSummaryDto, GetFundSummaryRequest>({
      query: ({ fundId, ...scope }) => ({
        url: withScopeQuery(`/funds/${encodeURIComponent(fundId)}/summary`, scope),
        method: 'GET',
      }),
      providesTags: (_result, _error, { fundId }) => [{ type: 'FundInvestors', id: fundId }],
    }),
  }),
});

export const {
  useGetFundsDashboardSummaryQuery,
  useGetFundsDashboardRecordsQuery,
  useGetFundInvestorsQuery,
  useGetFundSummaryQuery,
} = fundManagementApi;
